import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Projekt, Zuweisung } from '../types';
import { cn } from '../lib/utils';
interface ProjectBlockProps {
  projekt: Projekt;
  zuweisung: Zuweisung;
  weekWidth: number; // Width of one week column in px
  startOffset: number; // Offset from start of matrix in weeks
  isOverbooked?: boolean;
  onClick?: () => void;
}
export function ProjectBlock({
  projekt,
  zuweisung,
  weekWidth,
  startOffset,
  isOverbooked,
  onClick
}: ProjectBlockProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const duration = zuweisung.endKw - zuweisung.startKw;
  const width = duration * weekWidth;
  const left = (zuweisung.startKw - startOffset) * weekWidth;
  // Don't render if outside visible range (simplified check)
  if (width <= 0) return null;
  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX,
      y: e.clientY
    });
  };
  return <>
      <div className={cn('absolute h-8 rounded-md text-[10px] font-medium leading-none flex items-center px-2 truncate cursor-pointer select-none shadow-sm transition-all duration-200 group z-10', 'hover:scale-[1.02] hover:shadow-md hover:z-20', isOverbooked && 'ring-2 ring-destructive ring-offset-1 animate-pulse')} style={{
      background: `linear-gradient(to bottom, ${projekt.farbe}dd, ${projekt.farbe})`,
      color: '#fff',
      width: `${width - 4}px`,
      left: `${left + 2}px`,
      top: '6px',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
    }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onMouseMove={handleMouseMove} onClick={e => {
      e.stopPropagation();
      onClick?.();
    }} data-id="element-253">
        <span className="truncate w-full" data-id="element-254">
          {projekt.projektnummer.split('-')[1] || projekt.projektnummer} •{' '}
          {projekt.name}
        </span>

        {/* Utilization badge if < 100% */}
        {zuweisung.auslastungProzent < 100 && <span className="ml-1 text-[9px] opacity-80 bg-black/20 px-1 rounded" data-id="element-255">
            {zuweisung.auslastungProzent}%
          </span>}
      </div>

      {isHovered && createPortal(<div className="fixed pointer-events-none" style={{
      left: mousePos.x + 16,
      top: mousePos.y + 16,
      zIndex: 99999
    }} data-id="element-256">
            <div className="bg-popover/95 backdrop-blur-sm text-popover-foreground rounded-lg border shadow-xl p-4 w-72 text-xs animate-in fade-in zoom-in-95 duration-200" data-id="element-257">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b" data-id="element-258">
                <div className="w-3 h-3 rounded-full shadow-sm" style={{
            backgroundColor: projekt.farbe
          }} data-id="element-259" />
                <div className="font-bold text-sm" data-id="element-260">{projekt.projektnummer}</div>
                {isOverbooked && <span className="ml-auto bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded text-[10px] font-bold animate-pulse" data-id="element-261">
                    ÜBERBUCHT
                  </span>}
              </div>

              <div className="font-semibold text-base mb-1" data-id="element-262">{projekt.name}</div>
              <div className="text-muted-foreground mb-4" data-id="element-263">{projekt.kunde}</div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-4 bg-muted/30 p-3 rounded-md" data-id="element-264">
                <div data-id="element-265">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5" data-id="element-266">
                    Phase
                  </div>
                  <div className="font-medium" data-id="element-267">{zuweisung.phase}</div>
                </div>
                <div data-id="element-268">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5" data-id="element-269">
                    Auslastung
                  </div>
                  <div className={cn('font-medium', isOverbooked ? 'text-destructive font-bold' : '')} data-id="element-270">
                    {zuweisung.auslastungProzent}%
                  </div>
                </div>
                <div className="col-span-2" data-id="element-271">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5" data-id="element-272">
                    Zeitraum
                  </div>
                  <div className="font-medium flex items-center gap-2" data-id="element-273">
                    <span className="bg-background px-1.5 py-0.5 rounded border" data-id="element-274">
                      KW {zuweisung.startKw}
                    </span>
                    <span className="text-muted-foreground" data-id="element-275">→</span>
                    <span className="bg-background px-1.5 py-0.5 rounded border" data-id="element-276">
                      KW {zuweisung.endKw}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-[10px] text-center text-muted-foreground border-t pt-2" data-id="element-277">
                Klicken für Details
              </div>
            </div>
          </div>, document.body)}
    </>;
}